import axios from 'axios';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import StyledLink from '@ui/atoms/StyledLink';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import { interpolate } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { get } from '@utils/indexedDb';
import { getStateName } from '@utils/intake/stateRequirements';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowBailoutStepProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme/FolxTheme';

const ContentGridItem = styled(CenteredGridItem)`
	padding: ${theme.spacing.lgxl} 0;
`;

const TextSubtitle = styled(BasicText)`
	padding-top: ${theme.spacing.lgxl};
`;

const SignUpButton = styled(Button)`
	margin-top: ${theme.spacing.xxl};
`;

const NavLink = styled(StyledLink)`
	padding-top: ${theme.spacing.lg};
`;

const ActionWrapper = styled(FlexColumn)`
	justify-content: center;
	align-items: center;
`;

interface IStateIneligibilityStepProps extends IFlowBailoutStepProps {}

interface User {
	email?: string;
	stateOfResidence?: string;
}

const StateIneligibilityStep: FC<IStateIneligibilityStepProps> = () => {
	const [user, setUser] = useState({} as User);
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitSuccess, setSubmitSuccess] = useState(false);

	useEffect(() => {
		get('user').then((data) =>
			setUser({
				stateOfResidence: data.stateOfResidence,
				email: data.email,
			})
		);
	}, []);

	const context = useContext(FlowContext);
	const { destinationFlow: flowId } = context;

	const {
		handleSubmit,
		formState: { errors, isSubmitted },
	} = useForm();

	const onSubmit = async (e: any) => {
		setSubmitting(true);
		try {
			const newsletterResponse = await axios.post(ApiEndpoints.leadCapture, {
				email: user.email,
				state: user.stateOfResidence,
				service: flowId,
			});
			if (newsletterResponse.status == 200) {
				setSubmitSuccess(true);
			} else {
				setSubmitSuccess(false);
			}
		} catch (err: any) {
			console.log(`Error calling /contact endpoint: ${JSON.stringify(err)}`);
			setSubmitSuccess(false);
		} finally {
			setSubmitted(true);
			setSubmitting(false);
		}
	};

	interface IOnboardingStateIneligibilityContent {
		content: {
			shared_member_onboarding: {
				button_text: string;
				initial_header: string;
				initial_body: string;
				success_header: string;
				success_body: string;
				failure_header: string;
				failure_body: string;
			};
		};
	}

	const { content } = useStaticQuery<IOnboardingStateIneligibilityContent>(
		graphql`
			{
				content: butterPage(slug: { eq: "state-ineligibility" }) {
					shared_member_onboarding {
						button_text
						initial_header
						initial_body
						success_header
						success_body
						failure_header
						failure_body
					}
				}
			}
		`
	);

	return (
		<ContentGridItem>
			<form onSubmit={handleSubmit(onSubmit)}>
				<HeadingText>
					{submitted
						? submitSuccess
							? content.shared_member_onboarding.success_header
							: content.shared_member_onboarding.failure_header
						: content.shared_member_onboarding.initial_header}
				</HeadingText>
				<TextSubtitle>
					{submitted
						? submitSuccess
							? interpolate(content.shared_member_onboarding.success_body, {
									state: getStateName(user.stateOfResidence),
							  })
							: content.shared_member_onboarding.failure_body
						: content.shared_member_onboarding.initial_body}
				</TextSubtitle>
				<ActionWrapper>
					{!(submitted && submitSuccess) ? (
						<SignUpButton
							type="submit"
							label={content.shared_member_onboarding.button_text}
							disabled={submitting}
						/>
					) : (
						<></>
					)}
					<NavLink to="/" color="cyan" tint="darken40" variant="bold">
						Return to FOLX
					</NavLink>
				</ActionWrapper>
			</form>
		</ContentGridItem>
	);
};

export default StateIneligibilityStep;
